import maxTagDimensions from '@flame-frontend-utils/tiptap/configs/river/maxTagDimensions.json';
import { ReactNode } from 'react';
import { getResizedImageSrcSet, getResizedImageUrl } from '@flame-frontend-utils/tiptap';
import { Duration, intervalToDuration } from 'date-fns';
import noun from 'plural-ru';
import { Page } from '../../components/Page';
import { tw } from '../../styles/tw';
import { layout } from '../../styles/layout';
import { MediaPostListByTagQuery } from './gql/MediaPostListByTag.document';
import { ROUTES } from '../../lib/ROUTES';

interface PersonTagPageProps {
  tag: NonNullable<MediaPostListByTagQuery['tag']>;
  children?: ReactNode;
}

const MAX_IMAGE_WIDTH = maxTagDimensions.horizontal.width;
const MAX_IMAGE_HEIGHT = maxTagDimensions.horizontal.height;

const PersonTagPage = ({ tag, children }: PersonTagPageProps) => {
  const age = getAgeString(tag.birthDate);

  return (
    <Page
      className={tw(layout, 'mx-auto my-0 flex-col justify-start')}
      documentTitle={tag.value}
      canonicalUrl={ROUTES.TAG_FILTER.$buildPath({ params: { id: tag.alias } })}
      overflowY="scroll"
      hideAdvertisement
    >
      <div className="mb-6 grid border-b border-blood pb-6 pt-5 [grid-template-areas:'header''image''birthday''description'] lg:grid-cols-2 lg:pt-6 lg:[grid-template-areas:'image_header''image_birthday''image_description'] lg:[grid-template-rows:auto_auto_1fr]">
        <h1 className="mb-6 text-xl [grid-area:header] lg:ml-6">{tag.value}</h1>
        {age ? <p className="mb-4 [grid-area:birthday] lg:ml-6">Возраст: {getAgeString(tag.birthDate)}</p> : null}
        {tag.description ? <p className="[grid-area:description] lg:ml-6">{tag.description}</p> : null}
        {tag.image ? (
          <div className="mb-6 justify-self-center [grid-area:image] lg:mb-0 lg:ml-0 lg:justify-self-start lg:pr-6">
            <img
              loading="lazy"
              alt={tag.image.alt ?? tag.image.source ?? tag.value}
              width={MAX_IMAGE_WIDTH}
              height={MAX_IMAGE_HEIGHT}
              src={getResizedImageUrl({
                resizeOrigin: PUBLIC_CONFIG.IMAGE_RESIZE_ORIGIN,
                originalUrl: tag.image.originalUrl,
                cropSettings: isCropSettingsMap(tag.image.cropSettings) ? tag.image.cropSettings.horizontal : undefined,
                width: MAX_IMAGE_WIDTH,
                height: MAX_IMAGE_HEIGHT,
              })}
              srcSet={getResizedImageSrcSet({
                resizeOrigin: PUBLIC_CONFIG.IMAGE_RESIZE_ORIGIN,
                originalUrl: tag.image.originalUrl,
                cropSettings: isCropSettingsMap(tag.image.cropSettings) ? tag.image.cropSettings.horizontal : undefined,
                width: MAX_IMAGE_WIDTH,
                height: MAX_IMAGE_HEIGHT,
              })}
            />
          </div>
        ) : null}
      </div>
      {children}
    </Page>
  );
};

function isCropSettingsMap(map: unknown): map is Record<keyof typeof maxTagDimensions, unknown> {
  return Boolean(map && typeof map === 'object');
}

function getAgeString(isoDate: string | null | undefined) {
  if (!isoDate) {
    return null;
  }

  const { years, months, days } = getAge(isoDate);
  let ageString: string | null = null;

  if (years && years > 0) {
    ageString = `${years} ${noun(years, 'год', 'года', 'лет')}`;
  } else if (months && months > 0) {
    ageString = `${months} ${noun(months, 'месяц', 'месяца', 'месяцев')}`;
  } else if (days && days > 0) {
    ageString = `${days} ${noun(days, 'день', 'дня', 'дней')}`;
  }

  return ageString;
}

function getAge(isoDate: string): Duration {
  const birthDate = getStartOfDayInLocalTime(isoDate);
  const today = new Date();

  return intervalToDuration({
    start: birthDate,
    end: today,
  });
}

function getStartOfDayInLocalTime(isoDate: string): Date {
  const parts = isoDate.split('-');

  const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  date.setHours(0, 0, 0, 0);

  return date;
}

export { PersonTagPage };
